import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
// @ts-ignore
import WebViewer from '@pdftron/webviewer';
import {
  getWebViewerContainerSx,
  getWebViewerInitOptions,
  prepareWebViewerInstance,
  setupWebViewerOnDocumentLoaded,
} from 'core/services/webViewerSetup';

export interface PdfViewerDialogProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string | null;
  title?: string;
}

export const PdfViewerDialog: React.FC<PdfViewerDialogProps> = ({
  open,
  onClose,
  pdfUrl,
  title,
}) => {
  const theme = useTheme();
  const isTabletViewport = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const viewerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !pdfUrl) {
      return undefined;
    }

    const dispose = () => {
      if (instanceRef.current) {
        try {
          instanceRef.current.UI.dispose();
        } catch {
          /* ignore */
        }
        instanceRef.current = null;
      }
      setIsViewerReady(false);
    };

    const timeout = setTimeout(() => {
      dispose();
      setError(null);

      if (!viewerRef.current) {
        return;
      }

      WebViewer(
        getWebViewerInitOptions('viewOnly', pdfUrl) as any,
        viewerRef.current
      )
        .then((inst) => {
          instanceRef.current = inst;
          inst.Core.annotationManager.enableReadOnlyMode();
          prepareWebViewerInstance(inst, 'viewOnly');
          inst.Core.documentViewer.addEventListener('documentLoaded', () => {
            setupWebViewerOnDocumentLoaded(inst, 'viewOnly');
          });
          inst.Core.documentViewer.addEventListener('documentLoadError', () => {
            setError('Failed to load PDF. Try opening it in a new tab.');
          });
          setIsViewerReady(true);
        })
        .catch(() => {
          setError('Failed to initialize PDF viewer.');
        });
    }, 100);

    return () => {
      clearTimeout(timeout);
      dispose();
    };
  }, [open, pdfUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="pdf-viewer-title"
    >
      <DialogTitle
        id="pdf-viewer-title"
        sx={{
          fontFamily: 'Gotham',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        {title}
        <IconButton onClick={onClose} aria-label="Close document viewer" size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2, position: 'relative' }}>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity="error"
              action={
                pdfUrl ? (
                  <Button color="inherit" size="small" href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    Open in new tab
                  </Button>
                ) : undefined
              }
            >
              {error}
            </Alert>
          </Box>
        )}
        <Box sx={getWebViewerContainerSx(isTabletViewport, 'modal')}>
        {open && !isViewerReady && !error && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: '#446388' }} />
            <Typography variant="body2" sx={{ mt: 2, fontFamily: 'Gotham' }}>
              Loading document...
            </Typography>
          </Box>
        )}
        <div ref={viewerRef} style={{ height: '100%', width: '100%' }} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
